// Pure helpers for the Cloud scans page: depth presets, request building and per-account summaries.

export const DEFAULT_CAPABILITIES = {
  providers: ['aws', 'azure', 'gcp', 'kubernetes'],
  engines: ['cloud_audit', 'prowler', 'scoutsuite'],
  scan_types: ['cloud_aws_audit', 'cloud_multi_audit'],
  default_scan_type: 'cloud_aws_audit',
  categories: ['security', 'cost', 'reliability', 'performance'],
  min_severity: ['critical', 'high', 'medium', 'low', 'info'],
  report_formats: ['html', 'sarif', 'markdown']
};

export const PROVIDER_LABEL = { aws: 'AWS', azure: 'Azure', gcp: 'GCP', kubernetes: 'Kubernetes' };
export const providerLabel = (p) => PROVIDER_LABEL[p] || String(p || 'aws').toUpperCase();

export const DEPTHS = [
  { id: 'quick', title: 'Quick', desc: 'Security only, high severity and above.', meta: 'HTML report · chosen regions',
    categories: ['security'], minSeverity: 'high', allRegions: false, reportFormats: ['html'] },
  { id: 'balanced', title: 'Balanced', desc: 'Security, cost and reliability, medium and above.', meta: 'HTML + SARIF · chosen regions',
    categories: ['security', 'cost', 'reliability'], minSeverity: 'medium', allRegions: false, reportFormats: ['html', 'sarif'] },
  { id: 'deep', title: 'Deep', desc: 'Everything including performance, all severities.', meta: 'All formats · all regions',
    categories: ['security', 'cost', 'reliability', 'performance'], minSeverity: 'low', allRegions: true, reportFormats: ['html', 'sarif', 'markdown'] }
];

export const DEFAULT_FORM = {
  provider: 'aws', engines: ['cloud_audit'], awsProfile: '', roleArn: '', regions: 'us-east-1', allRegions: false,
  categories: ['security', 'cost', 'reliability'], minSeverity: 'medium', reportFormats: ['html', 'sarif'],
  remediation: true, exportFixes: false, timeout: 3600
};

export const applyDepth = (form, depthId) => {
  const d = DEPTHS.find((x) => x.id === depthId) || DEPTHS[1];
  return { ...form, categories: d.categories, minSeverity: d.minSeverity, allRegions: d.allRegions, reportFormats: d.reportFormats };
};

// One POST body. Multi-engine runs and every non-AWS provider use the multi-cloud scan type.
export function buildCloudRequest(form, capabilities, userTargetId) {
  if (!Array.isArray(form.engines) || form.engines.length === 0) throw new Error('Choose at least one audit engine.');
  const multi = form.provider !== 'aws' || form.engines.length > 1;
  return {
    user_target: userTargetId,
    scan_type: multi ? 'cloud_multi_audit' : capabilities?.default_scan_type || 'cloud_aws_audit',
    options: {
      cloud_provider: form.provider, cloud_engines: form.engines,
      aws_profile: form.awsProfile || undefined, aws_role_arn: form.roleArn || undefined,
      cloud_audit_regions: form.allRegions ? undefined : form.regions || undefined, cloud_audit_all_regions: form.allRegions,
      cloud_audit_categories: form.categories, cloud_audit_min_severity: form.minSeverity, cloud_audit_report_formats: form.reportFormats,
      cloud_audit_remediation: form.remediation, cloud_audit_export_fixes: form.exportFixes, cloud_audit_timeout: Number(form.timeout) || 3600
    }
  };
}

// Same keys as the old page, so links such as /cloud-scans?account=profile:acme keep working.
export const accountKey = (scan) => {
  const profile = String(scan?.options?.aws_profile || '').trim();
  const role = String(scan?.options?.aws_role_arn || '').trim();
  return profile ? `profile:${profile}` : role ? `role:${role}` : 'default';
};
export const accountName = (scan) => {
  const profile = String(scan?.options?.aws_profile || '').trim();
  const role = String(scan?.options?.aws_role_arn || '').trim();
  return profile || (role ? role.split('/').pop() : '') || 'Default credentials';
};
export const accountMeta = (scan) => {
  const o = scan?.options || {};
  const where = o.cloud_audit_all_regions ? 'all regions' : o.cloud_audit_regions || '';
  const engines = Array.isArray(o.cloud_engines) && o.cloud_engines.length ? o.cloud_engines.join(', ') : '';
  return [providerLabel(o.cloud_provider), where, engines].filter(Boolean).join(' · ');
};

// One card per account: its newest scan, whether something is running, and what the last finished audit found.
export function buildAccounts(scans, findings) {
  const bySev = {};
  findings.forEach((f) => { const m = (bySev[String(f.scan)] = bySev[String(f.scan)] || {}); m[f.severity] = (m[f.severity] || 0) + 1; });
  const map = new Map();
  [...scans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).forEach((s) => {
    const k = accountKey(s);
    const a = map.get(k) || { key: k, name: accountName(s), meta: accountMeta(s), provider: s.options?.cloud_provider || 'aws', latest: s, live: null, lastDone: null, runs: 0 };
    a.runs += 1;
    if (!a.live && ['running', 'pending'].includes(s.status)) a.live = s;
    if (!a.lastDone && ['completed', 'finished'].includes(s.status)) a.lastDone = s;
    map.set(k, a);
  });
  return [...map.values()].map((a) => {
    const sev = a.lastDone ? bySev[String(a.lastDone.id)] || {} : {};
    const state = a.live ? 'scanning' : !a.lastDone ? 'no-audit' : sev.critical ? 'action' : sev.high ? 'review' : 'healthy';
    return { ...a, sev, state };
  });
}

export const ACCOUNT_STATE = {
  scanning: { label: 'SCANNING', color: '#4ade80', border: '#22c55e' },
  action: { label: 'ACTION NEEDED', color: '#fca5a5', border: 'rgba(239,68,68,.45)' },
  review: { label: 'REVIEW', color: '#fdba74', border: 'rgba(249,115,22,.4)' },
  healthy: { label: 'HEALTHY', color: '#4ade80', border: null },
  'no-audit': { label: 'NO AUDIT YET', color: '#94a3b8', border: null }
};
